import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../services/api";
import Layout from "../common/Layout";
import ViewLead from "../leads/ViewLead";
import { InlineWidget } from "react-calendly";

/* ============================================================
   DATE / TIME HELPERS  (unchanged from your version)
   ============================================================ */

function parseSafeDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  let text = String(value).trim();
  if (!text) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const [year, month, day] = text.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  text = text.replace(/(\.\d{3})\d+(Z|[+-]\d{2}:\d{2})$/, "$1$2");
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDisplayDate(value) {
  if (!value) return "N/A";

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    const [year, month, day] = value.trim().split("-").map(Number);
    const date = new Date(year, month - 1, day);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-US", {
      weekday: "short", month: "short", day: "numeric", year: "numeric",
    });
  }

  const date = parseSafeDate(value);
  if (!date) return "N/A";
  return date.toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric", year: "numeric",
  });
}

/*
 * Time-only formatter.
 *
 * IMPORTANT: when the input is a bare "HH:mm[:ss]" string, we treat
 * it as a wall-clock time and format it as-is. When it is a full
 * ISO string, we let the Date object handle the offset.
 *
 * The backend already returns user-local wall-clock strings in
 * `userStart` / `userEnd` / `microsoftStart` / `microsoftEnd`, so
 * bare parsing is the correct branch for those fields.
 */
function formatDisplayTime(value) {
  if (!value) return "N/A";
  const text = String(value).trim();
  if (!text) return "N/A";

  if (/^(0?[1-9]|1[0-2]):[0-5]\d\s?(AM|PM)$/i.test(text)) return text;

  const plainTimeMatch = text.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (plainTimeMatch) {
    const hours = Number(plainTimeMatch[1]);
    const minutes = Number(plainTimeMatch[2]);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      const date = new Date(2000, 0, 1, hours, minutes, 0);
      return date.toLocaleTimeString("en-US", {
        hour: "numeric", minute: "2-digit",
      });
    }
  }

  const parsed = parseSafeDate(text);
  if (!parsed) return "N/A";
  return parsed.toLocaleTimeString("en-US", {
    hour: "numeric", minute: "2-digit",
  });
}

function getTimePart(value) {
  if (!value) return "";
  const text = String(value).trim();
  const isoMatch = text.match(/T(\d{2}):(\d{2})(?::\d{2})?/);
  if (isoMatch) return `${isoMatch[1]}:${isoMatch[2]}`;
  const timeMatch = text.match(/^(\d{1,2}):(\d{2})/);
  if (timeMatch) {
    return `${String(Number(timeMatch[1])).padStart(2, "0")}:${timeMatch[2]}`;
  }
  return text;
}

function getTodayString() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addDays(dateString, days) {
  const date = new Date(`${dateString}T00:00:00`);
  date.setDate(date.getDate() + days);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/* ============================================================
   DEMO STATUS  (unchanged)
   ============================================================ */

function DemoStatus({ status, date, time }) {
  const map = {
    Scheduled: "demo-status-scheduled",
    Completed: "demo-status-completed",
    Cancelled: "demo-status-cancelled",
    Rescheduled: "demo-status-rescheduled",
    "Not Booked": "demo-status-not-booked",
  };
  const className = map[status] || "demo-status-not-booked";
  return (
    <div className="demo-status">
      <span className={className}>{status || "Not Booked"}</span>
      {date && (
        <span className="demo-date-time">
          {date}
          {time && ` at ${time}`}
        </span>
      )}
    </div>
  );
}

/* ============================================================
   SLOT TOOLTIP CSS — injected once
   ============================================================ */

const SLOT_TOOLTIP_CSS = `
.ms-slot {
  position: relative;
  min-height: 42px;
  width: 100%;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: transform 0.08s ease, box-shadow 0.08s ease;
  padding: 6px 8px;
}
.ms-slot:disabled { cursor: not-allowed; }
.ms-slot:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(0,0,0,0.10);
}
.ms-slot--available {
  background: #e7f1ff;
  border-color: #b9d4fb;
  color: #0b3d91;
}
.ms-slot--available:hover:not(:disabled) {
  background: #d6e8ff;
  border-color: #8fbcf7;
}
.ms-slot--selected {
  background: #0b5ed7;
  border-color: #0a53be;
  color: #fff;
}
.ms-slot--busy {
  background: #fde8e8;
  border-color: #f5b5b5;
  color: #a01212;
  opacity: 0.95;
}
.ms-slot__tooltip {
  position: absolute;
  left: 50%;
  bottom: calc(100% + 8px);
  transform: translateX(-50%);
  width: 260px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #1f2430;
  color: #fff;
  text-align: left;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.45;
  box-shadow: 0 8px 24px rgba(0,0,0,0.28);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
  z-index: 30;
  white-space: normal;
}
.ms-slot--busy:hover .ms-slot__tooltip,
.ms-slot--busy:focus .ms-slot__tooltip {
  opacity: 1;
}
.ms-slot__tooltip-title {
  display: block;
  margin-bottom: 4px;
  font-weight: 600;
  font-size: 12.5px;
}
.ms-slot__tooltip-row {
  display: block;
}

.ms-slot--closed {
  background: #f1f3f5;
  border-color: #dfe3e8;
  color: #8a94a6;
  cursor: not-allowed;
}
.ms-slot--closed:hover {
  transform: none;
  box-shadow: none;
}
`;

/* ============================================================
   MAIN COMPONENT
   ============================================================ */

function BookDemo() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [lead, setLead] = useState(null);
  const [loadingLead, setLoadingLead] = useState(true);

  const [booking, setBooking] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const [showViewLead, setShowViewLead] = useState(false);

  const today = useMemo(() => getTodayString(), []);
  const [selectedDate, setSelectedDate] = useState(today);

  const [services, setServices] = useState([]);
  const [servicesOwnerAppId, setServicesOwnerAppId] = useState(null);
  const [servicesError, setServicesError] = useState("");
  const [loadingServices, setLoadingServices] = useState(false);

  const [selectedServiceId, setSelectedServiceId] = useState("");

  const [availability, setAvailability] = useState(null);
  const [availabilityKey, setAvailabilityKey] = useState(null);
  const [availabilityError, setAvailabilityError] = useState("");
  const [staffError, setStaffError] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [bookingApps, setBookingApps] = useState([]);
  const [selectedBookingApp, setSelectedBookingApp] = useState(null);

  const [selectedContactId, setSelectedContactId] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState("");

  /* ==========================================================
     LOAD LEAD
     ========================================================== */

  useEffect(() => {
    let mounted = true;
    const loadLead = async () => {
      setLoadingLead(true);
      setError("");
      try {
        const data = await api.getLead(id);
        if (mounted) setLead(data);
      } catch (err) {
        if (mounted) {
          setError(err?.response?.data?.error || "Failed to load lead");
        }
      } finally {
        if (mounted) setLoadingLead(false);
      }
    };
    loadLead();
    return () => { mounted = false; };
  }, [id]);

  /* ==========================================================
     LOAD BOOKING APPS
     ========================================================== */

  useEffect(() => {
    let mounted = true;
    api
      .getBookingApps()
      .then((list) => {
        if (!mounted) return;
        const apps = Array.isArray(list) ? list : [];
        setBookingApps(apps);

        const source = String(
          lead?.lead_source || lead?.leadSource || "",
        ).trim();

        const matches = apps.filter((a) =>
          (a.leadSources || []).some(
            (v) => String(v).toLowerCase() === source.toLowerCase(),
          ),
        );

        if (matches.length === 1) setSelectedBookingApp(matches[0]);
        else if (matches.length > 1) setSelectedBookingApp(null);
        else if (apps.length === 1) setSelectedBookingApp(apps[0]);
        else setSelectedBookingApp(null);
      })
      .catch((err) => console.error("Failed to load booking apps:", err));

    return () => { mounted = false; };
  }, [lead]);

  const availableBookingApps = useMemo(() => {
    const source = String(lead?.lead_source || lead?.leadSource || "").trim();
    const matches = bookingApps.filter((app) =>
      (app.leadSources || []).some(
        (value) => String(value).toLowerCase() === source.toLowerCase(),
      ),
    );
    if (matches.length > 0) return matches;
    return bookingApps.length === 1 ? bookingApps : [];
  }, [bookingApps, lead]);

  /* ==========================================================
     LOAD MICROSOFT SERVICES FOR SELECTED BOOKING APP

     Owner-tagged: servicesOwnerAppId tells us which booking app
     the current `services` array belongs to. Any consumer that
     reads `services` must first check the owner id matches.
     ========================================================== */

  useEffect(() => {
    let mounted = true;

    const loadServices = async () => {
      // Not a Microsoft app -> hard reset, no error message
      if (selectedBookingApp?.provider !== "microsoft") {
        if (mounted) {
          setServices([]);
          setServicesOwnerAppId(null);
          setSelectedServiceId("");
          setAvailability(null);
          setAvailabilityKey(null);
          setSelectedSlot(null);
          setServicesError("");
          setAvailabilityError("");
          setStaffError("");
        }
        return;
      }

      if (!selectedBookingApp?.id) {
        if (mounted) {
          setServices([]);
          setServicesOwnerAppId(null);
          setSelectedServiceId("");
          setServicesError(
            "This booking app is not linked to a Microsoft Bookings business.",
          );
        }
        return;
      }

      // Synchronous invalidate on every app switch
      if (mounted) {
        setServices([]);
        setServicesOwnerAppId(null);
        setSelectedServiceId("");
        setAvailability(null);
        setAvailabilityKey(null);
        setSelectedSlot(null);
        setServicesError("");
        setAvailabilityError("");
        setStaffError("");
        setLoadingServices(true);
      }

      try {
        const data = await api.getMicrosoftBookingServices(
          selectedBookingApp.id,
        );

        if (!mounted) return;

        const list = Array.isArray(data?.services) ? data.services : [];

        if (!list.length) {
          setServices([]);
          setServicesOwnerAppId(selectedBookingApp.id);
          setSelectedServiceId("");
          setServicesError(
            "No services are configured under this Microsoft Bookings business. Please contact your administrator.",
          );
          return;
        }

        setServices(list);
        setServicesOwnerAppId(selectedBookingApp.id);
        setSelectedServiceId(String(list[0].id)); // default first service
      } catch (err) {
        if (!mounted) return;
        console.error("Failed to load Microsoft services:", err);
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Unable to reach this Microsoft Bookings business. Please verify the booking app configuration.";
        setServices([]);
        setServicesOwnerAppId(null);
        setSelectedServiceId("");
        setServicesError(msg);
      } finally {
        if (mounted) setLoadingServices(false);
      }
    };

    loadServices();
    return () => { mounted = false; };
  }, [selectedBookingApp?.id, selectedBookingApp?.provider]);

  /* ==========================================================
     DEFAULT CONTACT
     ========================================================== */

  useEffect(() => {
    const contacts = Array.isArray(lead?.contacts) ? lead.contacts : [];
    if (contacts.length && !selectedContactId) {
      setSelectedContactId(
        String(contacts[0].id || contacts[0].contact_id || ""),
      );
    }
  }, [lead, selectedContactId]);

  /* ==========================================================
     LOAD MICROSOFT AVAILABILITY

     Guard order matters:
       1. must be a Microsoft app
       2. must have a bookingAppId
       3. services must belong to THIS app (owner check)
       4. selected service must exist in that list
       5. must have a date
     ========================================================== */

  useEffect(() => {
    let mounted = true;

    const loadAvailability = async () => {
      const isMicrosoft =
        selectedBookingApp?.provider === "microsoft" &&
        !!selectedBookingApp?.id;

      const servicesBelongHere =
        !!selectedBookingApp?.id &&
        servicesOwnerAppId === selectedBookingApp.id;

      const serviceExists =
        servicesBelongHere &&
        services.some((s) => String(s.id) === String(selectedServiceId));

      if (
        !isMicrosoft ||
        !servicesBelongHere ||
        !serviceExists ||
        !selectedDate ||
        !selectedServiceId
      ) {
        if (mounted) {
          setAvailability(null);
          setAvailabilityKey(null);
          setSelectedSlot(null);
          setAvailabilityError("");
          setStaffError("");
        }
        return;
      }

      const currentKey = `${selectedBookingApp.id}|${selectedServiceId}|${selectedDate}`;

      setLoadingSlots(true);
      setSelectedSlot(null);
      setAvailabilityError("");
      setStaffError("");

      try {
        const data = await api.getMicrosoftBookingAvailability(
          selectedDate,
          selectedServiceId,
          selectedBookingApp.id,
        );

        if (!mounted) return;

        setAvailability(data);
        setAvailabilityKey(currentKey);

        const assigned = Array.isArray(data?.assignedStaff)
          ? data.assignedStaff
          : [];

        if (!assigned.length) {
          setStaffError(
            "The selected Microsoft Bookings service has no assigned staff. Please choose another service or contact your administrator.",
          );
        }
      } catch (err) {
        if (!mounted) return;
        console.error("Failed to load Microsoft availability:", err);
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Unable to load availability for this date. Please try again.";
        setAvailability(null);
        setAvailabilityKey(null);
        setAvailabilityError(msg);
      } finally {
        if (mounted) setLoadingSlots(false);
      }
    };

    loadAvailability();
    return () => { mounted = false; };
  }, [
    selectedDate,
    selectedServiceId,
    selectedBookingApp?.id,
    selectedBookingApp?.provider,
    servicesOwnerAppId,
    services,
  ]);

  /* ==========================================================
     DATE OPTIONS
     ========================================================== */

  const dateOptions = useMemo(
    () => Array.from({ length: 14 }, (_, i) => addDays(today, i)),
    [today],
  );

  /* ==========================================================
     DERIVED SLOT VIEW

     Availability is only trusted when its key matches the
     current (app, service, date) tuple. Otherwise we render
     loading / empty states, never stale data.
     ========================================================== */

  const currentAvailabilityKey = useMemo(() => {
    if (
      !selectedBookingApp?.id ||
      !selectedServiceId ||
      !selectedDate
    ) {
      return null;
    }
    return `${selectedBookingApp.id}|${selectedServiceId}|${selectedDate}`;
  }, [selectedBookingApp?.id, selectedServiceId, selectedDate]);

  const availabilityIsFresh =
    !!availability &&
    !!availabilityKey &&
    availabilityKey === currentAvailabilityKey;

   
  
  const calendarSlots = useMemo(() => {
  if (!availabilityIsFresh) return [];

  const free = Array.isArray(availability?.slots)
    ? availability.slots.map((s) => ({ ...s, status: "available" }))
    : [];
  const busy = Array.isArray(availability?.busySlots)
    ? availability.busySlots.map((s) => ({ ...s, status: "busy" }))
    : [];
  const closed = Array.isArray(availability?.closedSlots)
    ? availability.closedSlots.map((s) => ({ ...s, status: "closed" }))
    : [];

  return [...free, ...busy, ...closed].sort((a, b) =>
    String(a.startUtc || "").localeCompare(String(b.startUtc || "")),
  );
}, [availability, availabilityIsFresh]);
  

  const availableCount = calendarSlots.filter(
    (s) => s.status === "available",
  ).length;
  const busyCount = calendarSlots.filter((s) => s.status === "busy").length;

  /* ==========================================================
     BOOK DEMO
     ========================================================== */

  const handleBooking = async () => {
    if (!selectedSlot) {
      setError("Please select a time.");
      return;
    }
    if (!lead) {
      setError("Lead information is not available.");
      return;
    }
    if (!selectedBookingApp?.id || !selectedServiceId) {
      setError("Booking configuration is incomplete.");
      return;
    }

    // Backend validates against userStart/userEnd, so send those
    const bookingStart = selectedSlot.userStart || selectedSlot.start;
    const bookingEnd = selectedSlot.userEnd || selectedSlot.end;

    if (!bookingStart || !bookingEnd) {
      setError("Selected booking slot is invalid. Please select another time.");
      return;
    }

    setBooking(true);
    setError("");
    setSuccess(null);

    try {
      const result = await api.bookMicrosoftDemo({
        leadId: Number(id),
        bookingAppId: selectedBookingApp.id,
        serviceId: selectedServiceId,
        contactId: selectedContactId || undefined,
        start: bookingStart,
        end: bookingEnd,
        notes: notes.trim(),
      });
      setSuccess(result);
    } catch (err) {
      console.error("Microsoft booking failed:", err);
      setError(err?.response?.data?.error || "Failed to book demo");
    } finally {
      setBooking(false);
    }
  };

  const handleCalendlyEvent = async (e) => {
    if (e?.data?.event !== "calendly.event_scheduled") return;
    try {
      const payload = e.data.payload || {};
      const start =
        payload?.event?.start_time ||
        payload?.event?.startTime ||
        payload?.invitee?.start_time;
      if (!start) return;
      const d = new Date(start);
      if (Number.isNaN(d.getTime())) return;
      await api.bookDemo(
        Number(id),
        d.toLocaleDateString("en-CA"),
        d.toLocaleTimeString("en-US", {
          hour: "numeric", minute: "2-digit", hour12: true,
        }),
        "Calendly booking",
        payload?.event?.uri || payload?.event?.uuid || null,
      );
      const fresh = await api.getLead(id);
      setLead(fresh);
      setSuccess({ message: "Demo booked successfully through Calendly." });
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          "Calendly booking was completed, but CRM could not update the demo record.",
      );
    }
  };

  const handleViewLead = () => setShowViewLead(true);
  const handleCloseViewLead = () => setShowViewLead(false);
  const handleViewLeadChanged = async () => {
    try {
      const data = await api.getLead(id);
      if (data) setLead(data);
    } catch (err) {
      console.error("Failed to refresh lead:", err);
    }
  };

  /* ==========================================================
     LOADING / NOT FOUND
     ========================================================== */

  if (loadingLead) {
    return (
      <Layout title="Book Demo" subtitle="Microsoft Bookings">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <div>Loading lead...</div>
        </div>
      </Layout>
    );
  }

  if (!lead) {
    return (
      <Layout title="Book Demo" subtitle="Microsoft Bookings">
        <div className="error-container">
          <div className="error-icon">
            <i className="fas fa-exclamation-circle"></i>
          </div>
          <div className="error-message">{error || "Lead not found"}</div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate("/leads")}
          >
            Back to Leads
          </button>
        </div>
      </Layout>
    );
  }

  /* ==========================================================
     SUCCESS
     ========================================================== */

  if (success) {
    const bookedLead = success.lead || success.bookedLead || null;
    const appointment =
      success.appointment ||
      success.booking ||
      success.microsoftAppointment ||
      null;

    const localStart =
      success.demoLocal?.start ||
      selectedSlot?.userStart ||
      selectedSlot?.start ||
      selectedDate;

    const bookedDate = formatDisplayDate(localStart);
    const bookedTime = formatDisplayTime(localStart);

    const bookingId =
      success.bookingId ||
      success.microsoftBookingId ||
      success.microsoft_booking_id ||
      appointment?.id ||
      appointment?.selfServiceAppointmentId ||
      "";

    const joinWebUrl =
      success.joinWebUrl ||
      success.join_url ||
      success.microsoftJoinUrl ||
      appointment?.joinWebUrl ||
      appointment?.anonymousJoinWebUrl ||
      "";

    return (
      <>
        <Layout title="Demo Scheduled" subtitle="Microsoft Bookings confirmation">
          <div className="card">
            <div style={{ textAlign: "center", padding: "30px 20px" }}>
              <div className="success-icon" style={{ marginBottom: "15px" }}>
                <i className="fas fa-check-circle"></i>
              </div>
              <h2 style={{ marginBottom: "8px" }}>Demo Scheduled</h2>
              <p className="text-muted" style={{ marginBottom: "22px" }}>
                Microsoft Bookings appointment created successfully.
              </p>

              <div
                style={{
                  background: "#f7f9fb",
                  border: "1px solid #e2e8ee",
                  borderRadius: "8px",
                  padding: "18px",
                  maxWidth: "600px",
                  margin: "0 auto 20px",
                }}
              >
                <strong>{bookedLead?.company || lead.company || "Customer"}</strong>
                <div style={{ marginTop: "10px" }}>{bookedDate}</div>
                <div>{bookedTime}</div>
                <div style={{ marginTop: "10px" }}>
                  <DemoStatus
                    status="Scheduled"
                    date={bookedDate !== "N/A" ? bookedDate : ""}
                    time={bookedTime !== "N/A" ? bookedTime : ""}
                  />
                </div>
              </div>

              {success.userTimeZone && (
                <div
                  className="text-muted"
                  style={{ fontSize: ".8rem", marginBottom: "15px" }}
                >
                  Time zone: {success.userTimeZone}
                </div>
              )}

              {joinWebUrl && (
                <div style={{ marginBottom: "20px" }}>
                  <a
                    href={joinWebUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-success"
                  >
                    <i className="fas fa-video"></i> Join Teams Meeting
                  </a>
                </div>
              )}

              {bookingId && (
                <div
                  style={{
                    fontSize: ".75rem",
                    color: "#7c8798",
                    marginBottom: "20px",
                    wordBreak: "break-all",
                  }}
                >
                  <strong>Microsoft Booking ID:</strong>
                  <br />
                  {bookingId}
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate("/leads")}
                >
                  Back to My Leads
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleViewLead}
                >
                  View Lead
                </button>
              </div>
            </div>
          </div>
        </Layout>

        {showViewLead && (
          <ViewLead
            leadId={Number(id)}
            embedded={true}
            onClose={handleCloseViewLead}
            onChanged={handleViewLeadChanged}
          />
        )}
      </>
    );
  }

  /* ==========================================================
     BOOKING SCREEN
     ========================================================== */

  const contacts = Array.isArray(lead.contacts) ? lead.contacts : [];
  const selectedContact =
    contacts.find(
      (c) =>
        String(c.id || c.contact_id) === String(selectedContactId),
    ) ||
    contacts[0] ||
    null;

  const selectedService =
    services.find(
      (s) => String(s.id) === String(selectedServiceId),
    ) || null;

  const isMicrosoftApp = selectedBookingApp?.provider === "microsoft";
  const servicesReady =
    isMicrosoftApp &&
    servicesOwnerAppId === selectedBookingApp?.id &&
    services.length > 0;

  return (
    <Layout
      title="Book Demo"
      subtitle={`Schedule demo for ${lead.company}`}
    >
      <style>{SLOT_TOOLTIP_CSS}</style>

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      <div className="lead-details-grid">
        {/* ==============================================
            CUSTOMER INFORMATION
            ============================================== */}
        <div className="card detail-card">
          <h3>Customer Information</h3>

          <div className="detail-row">
            <span className="label">Dealership</span>
            <span className="value">{lead.company || "N/A"}</span>
          </div>
          <div className="detail-row">
            <span className="label">Email</span>
            <span className="value">
              {selectedContact?.email || lead.email || "N/A"}
            </span>
          </div>
          <div className="detail-row">
            <span className="label">Contact</span>
            <span className="value">
              {selectedContact?.name ||
                [selectedContact?.first_name || selectedContact?.firstName,
                 selectedContact?.last_name || selectedContact?.lastName]
                  .filter(Boolean).join(" ") ||
                "N/A"}
            </span>
          </div>
          <div className="detail-row">
            <span className="label">Phone</span>
            <span className="value">
              {selectedContact?.work_phone ||
                selectedContact?.mobile_phone ||
                selectedContact?.workPhone ||
                selectedContact?.mobilePhone ||
                lead.main_line ||
                "N/A"}
            </span>
          </div>
        </div>

        {/* ==============================================
            BOOKING APP CHOOSER (only when ambiguous)
            ============================================== */}
        {availableBookingApps.length > 1 && (
          <div className="card detail-card">
            <h3>Choose Booking App</h3>
            <select
              className="form-control"
              value={selectedBookingApp?.id || ""}
              onChange={(e) =>
                setSelectedBookingApp(
                  availableBookingApps.find(
                    (a) => String(a.id) === e.target.value,
                  ) || null,
                )
              }
            >
              <option value="">Select booking app</option>
              {availableBookingApps.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name} (
                  {app.provider === "calendly"
                    ? "Calendly"
                    : "Microsoft Bookings"}
                  )
                </option>
              ))}
            </select>
          </div>
        )}

        {/* ==============================================
            CALENDLY
            ============================================== */}
        {selectedBookingApp?.provider === "calendly" ? (
          <div className="card detail-card">
            <h3>Book Demo — {selectedBookingApp.name}</h3>
            <p className="text-muted">
              Complete the booking below. CRM will update the demo date and
              status after Calendly confirms the appointment.
            </p>
            <InlineWidget
              url={selectedBookingApp.bookingUrl}
              styles={{ minWidth: "320px", height: "720px" }}
              prefill={{
                email: selectedContact?.email || lead?.email || "",
                name:
                  selectedContact?.name ||
                  [selectedContact?.first_name, selectedContact?.last_name]
                    .filter(Boolean).join(" ") ||
                  "",
              }}
            />
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate("/leads")}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="card detail-card">
            <h3>Book Microsoft Demo</h3>

            {/* ---- BOOKING APP NOT RESOLVED ---- */}
            {!selectedBookingApp && (
              <div className="empty-state">
                <i className="fas fa-info-circle"></i>
                <h3>No booking app selected</h3>
                <p>
                  Pick a booking app above to continue. If none is listed,
                  ask your administrator to map this lead source to a
                  Microsoft Bookings app.
                </p>
              </div>
            )}

            {/* ---- NOT A MICROSOFT APP ---- */}
            {selectedBookingApp &&
              selectedBookingApp.provider !== "microsoft" && (
                <div className="empty-state">
                  <i className="fas fa-exclamation-circle"></i>
                  <h3>Unsupported provider</h3>
                  <p>
                    This booking app is configured for{" "}
                    <strong>{selectedBookingApp.provider}</strong>. Microsoft
                    Bookings flow is unavailable.
                  </p>
                </div>
              )}

            {/* ---- MICROSOFT APP ---- */}
            {isMicrosoftApp && (
              <>
                {/* SERVICE */}
                <div className="form-group">
                  <label>Microsoft Bookings Service *</label>

                  {loadingServices ? (
                    <div className="text-muted" style={{ padding: "8px 0" }}>
                      <i className="fas fa-spinner fa-spin"></i> Loading
                      services...
                    </div>
                  ) : servicesError ? (
                    <div
                      className="error-message"
                      style={{ margin: 0 }}
                    >
                      <i className="fas fa-exclamation-circle"></i>{" "}
                      {servicesError}
                    </div>
                  ) : !servicesReady ? (
                    <div className="text-muted" style={{ padding: "8px 0" }}>
                      No services available for this booking app.
                    </div>
                  ) : (
                    <select
                      className="form-control"
                      value={selectedServiceId}
                      onChange={(e) => {
                        setSelectedServiceId(e.target.value);
                        setSelectedSlot(null);
                        setAvailability(null);
                        setAvailabilityKey(null);
                      }}
                      disabled={booking}
                    >
                      {services.map((service) => (
                        <option key={service.id} value={service.id}>
                          {service.displayName || service.name || service.id}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* STAFF */}
                <div className="form-group">
                  <label>Assigned Staff</label>
                  <input
                    className="form-control"
                    readOnly
                    value={
                      (
                        selectedService?.assignedStaff ||
                        availability?.assignedStaff ||
                        []
                      )
                        .map((staff) => staff.displayName || staff.id)
                        .join(", ") || "Assigned by Microsoft Bookings"
                    }
                  />
                  {staffError ? (
                    <small
                      className="text-muted"
                      style={{ color: "#a01212" }}
                    >
                      {staffError}
                    </small>
                  ) : (
                    <small className="text-muted">
                      Staff is controlled by Microsoft Bookings and cannot be
                      changed here.
                    </small>
                  )}
                </div>

                {/* CONTACT */}
                <div className="form-group">
                  <label>Customer Contact *</label>
                  <select
                    className="form-control"
                    value={selectedContactId}
                    onChange={(e) => setSelectedContactId(e.target.value)}
                    disabled={booking || !contacts.length}
                  >
                    {contacts.length ? (
                      contacts.map((contact, index) => (
                        <option
                          key={contact.id || contact.contact_id || index}
                          value={contact.id || contact.contact_id || ""}
                        >
                          {contact.name ||
                            [contact.first_name || contact.firstName,
                             contact.last_name || contact.lastName]
                              .filter(Boolean).join(" ") ||
                            `Contact ${index + 1}`}
                        </option>
                      ))
                    ) : (
                      <option value="">Primary lead contact</option>
                    )}
                  </select>
                </div>

                {/* DATE */}
                <div className="form-group">
                  <label>Demo Date</label>
                  <select
                    className="form-control"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    disabled={booking}
                  >
                    {dateOptions.map((date) => (
                      <option key={date} value={date}>
                        {formatDisplayDate(date)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* SLOTS */}
                <div className="form-group">
                  <label>
                    Calendar{" "}
                    {availabilityIsFresh && (
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        ({availableCount} available · {busyCount} busy)
                      </span>
                    )}
                  </label>

                  {loadingSlots || !servicesReady ? (
                    <div
                      className="loading-spinner"
                      style={{ minHeight: 120, padding: 20 }}
                    >
                      <div className="spinner"></div>
                      <div>Loading Microsoft availability...</div>
                    </div>
                  ) : availabilityError ? (
                    <div className="error-message" style={{ margin: 0 }}>
                      <i className="fas fa-exclamation-circle"></i>{" "}
                      {availabilityError}
                    </div>
                  ) : !availabilityIsFresh ? (
                    <div className="text-muted" style={{ padding: "8px 0" }}>
                      Select a service and date to view availability.
                    </div>
                  ) : calendarSlots.length === 0 ? (
                    <div className="empty-state">
                      <i className="fas fa-calendar-times"></i>
                      <h3>No slots on this date</h3>
                      <p>
                        There are no working hours or appointments on this
                        date. Please select another date.
                      </p>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(125px, 1fr))",
                        gap: 8,
                      }}
                    >
                      {calendarSlots.map((slot, index) => {
                        
                        
                       
                        const busy   = slot.status === "busy";
                        const closed = slot.status === "closed";
                        const selected = selectedSlot?.startUtc === slot.startUtc
                                      && selectedSlot?.endUtc   === slot.endUtc;

                        const cls = selected
                          ? "ms-slot ms-slot--selected"
                          : busy
                            ? "ms-slot ms-slot--busy"
                            : closed
                              ? "ms-slot ms-slot--closed"
                              : "ms-slot ms-slot--available";
                         
                        
                        const label = formatDisplayTime(
                          getTimePart(slot.userStart || slot.start),
                        );

                        return (
                          <button
                            type="button"
                            key={`${slot.startUtc}-${slot.endUtc}-${index}`}
                            className={cls}
                            onClick={() => {
                              if (!busy && !closed ) setSelectedSlot(slot);
                            }}
                            disabled={booking || busy || closed }
                            title={
                              busy
                                ? "Busy — hover for details"
                                : "Available — click to select"
                            }
                          >
                            {label}
                            {busy ? " · Busy" : ""}

                            {busy && (
                              <span className="ms-slot__tooltip" role="tooltip">
                                <strong className="ms-slot__tooltip-title">
                                  {slot.subject ||
                                    slot.serviceName ||
                                    "Busy"}
                                </strong>
                                <span className="ms-slot__tooltip-row">
                                  {formatDisplayTime(
                                    getTimePart(
                                      slot.userStart || slot.start,
                                    ),
                                  )}
                                  {" – "}
                                  {formatDisplayTime(
                                    getTimePart(slot.userEnd || slot.end),
                                  )}
                                </span>
                                {slot.serviceName &&
                                  slot.serviceName !== slot.subject && (
                                    <span className="ms-slot__tooltip-row">
                                      Service: {slot.serviceName}
                                    </span>
                                  )}
                                {slot.customerName && (
                                  <span className="ms-slot__tooltip-row">
                                    Customer: {slot.customerName}
                                  </span>
                                )}
                                {slot.staffName && (
                                  <span className="ms-slot__tooltip-row">
                                    Staff: {slot.staffName}
                                  </span>
                                )}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* NOTES */}
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional notes for the demo..."
                    disabled={booking}
                  />
                </div>

                {selectedSlot && (
                  <div
                    style={{
                      background: "#edf6f1",
                      border: "1px solid #d6e8de",
                      borderRadius: 8,
                      padding: 12,
                      marginBottom: 15,
                    }}
                  >
                    <strong>Selected:</strong>{" "}
                    {formatDisplayDate(
                      selectedSlot.userStart || selectedDate,
                    )}
                    {" at "}
                    {formatDisplayTime(
                      getTimePart(
                        selectedSlot.userStart || selectedSlot.start,
                      ),
                    )}
                  </div>
                )}

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate("/leads")}
                    disabled={booking}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleBooking}
                    disabled={
                      booking ||
                      loadingSlots ||
                      loadingServices ||
                      !selectedSlot ||
                      !servicesReady
                    }
                  >
                    {booking ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i> Booking...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-calendar-check"></i> Book Demo
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default BookDemo;